import { useCallback, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Review, UpdateReviewRequest, ListReviewsParams } from '../../../infrastructure/ReviewClient';
import { ReviewClient } from '../../../infrastructure/ReviewClient';

export const useReview = () => {
  const queryClient = useQueryClient();
  const reviewClient = new ReviewClient();
  const [error, setError] = useState<string | null>(null);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<Review[]>([]);
  const [allReviewsList, setAllReviewsList] = useState<Review[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Query for initial reviews with proper API
  const allReviewsQuery = useQuery({
    queryKey: ['reviews'],
    queryFn: async () => {
      const response = await reviewClient.getAllReviews({
        limit: 20,
        sortOrder: 'newest',
      });

      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Update local state when query data changes
  const queryData = allReviewsQuery.data;

  useEffect(() => {
    if (queryData) {
      setAllReviewsList(queryData.reviews || []);
      setNextToken(queryData.nextToken || null);
      setHasMore(queryData.hasMore || false);
    }
  }, [queryData]);

  // Function to load more reviews
  const loadMoreReviews = useCallback(async () => {
    if (!nextToken || isLoadingMore) {
      return;
    }

    try {
      setIsLoadingMore(true);
      setError(null);

      const params: ListReviewsParams = {
        limit: 20,
        sortOrder: 'newest',
      };

      if (nextToken) {
        params.nextToken = nextToken;
      }

      const response = await reviewClient.getAllReviews(params);

      // Append new reviews to existing list
      setAllReviewsList((prev) => [...prev, ...response.reviews]);
      setNextToken(response.nextToken || null);
      setHasMore(response.hasMore || false);

      return response;
    } catch (error) {
      setError('Failed to load more reviews');
      throw error;
    } finally {
      setIsLoadingMore(false);
    }
  }, [reviewClient, nextToken, isLoadingMore]);

  // Function to fetch reviews by productId (all statuses)
  const fetchReviewsByProductId = useCallback(
    async (productId: number) => {
      try {
        setIsLoadingSearch(true);
        setError(null);

        const fetchedReviews = await reviewClient.getAllReviewsByProductId(productId);
        setSearchResults(fetchedReviews);
        return fetchedReviews;
      } catch (error) {
        setError('Failed to fetch reviews for the specified product');
        setSearchResults([]);
        return [];
      } finally {
        setIsLoadingSearch(false);
      }
    },
    [reviewClient],
  );

  // Function to fetch all reviews
  const fetchAllReviews = useCallback(async (): Promise<Review[]> => {
    try {
      setError(null);
      const response = await reviewClient.getAllReviews();
      return response.reviews || [];
    } catch (error) {
      console.warn('API request failed, using dummy data');
      return [];
    }
  }, [reviewClient]);

  // Mutation for updating reviews
  const updateReviewMutation = useMutation({
    mutationFn: async ({ review, updateData }: { review: Review; updateData: UpdateReviewRequest }) => {
      return await reviewClient.updateReview(review.productId, review.reviewId, updateData);
    },
    onSuccess: (updatedReview) => {
      // Invalidate and refetch the reviews query
      queryClient.invalidateQueries({ queryKey: ['reviews'] });

      // Update search results if they exist
      setSearchResults((prev) =>
        prev.map((review) => (review.reviewId === updatedReview.reviewId ? updatedReview : review)),
      );
    },
    onError: () => {
      setError('Failed to update review');
    },
  });

  // Mutation for deleting reviews
  const deleteReviewMutation = useMutation({
    mutationFn: async ({ productId, reviewId }: { productId: number; reviewId: number }) => {
      return await reviewClient.deleteReview(productId, reviewId);
    },
    onSuccess: (_, { reviewId }) => {
      // Invalidate and refetch the reviews query
      queryClient.invalidateQueries({ queryKey: ['reviews'] });

      // Remove from search results if they exist
      setSearchResults((prev) => prev.filter((review) => review.reviewId !== reviewId));

      // Remove from all reviews list
      setAllReviewsList((prev) => prev.filter((review) => review.reviewId !== reviewId));
    },
    onError: () => {
      setError('Failed to delete review');
    },
  });

  // Function to update a review
  const updateReview = useCallback(
    async (review: Review) => {
      try {
        setError(null);

        const updateData: UpdateReviewRequest = {
          name: review.name,
          email: review.email,
          rating: review.rating,
          body: review.body,
          title: review.title,
          skinType: review.skinType,
          skinConcern: review.skinConcern,
          status: review.status,
        };

        await updateReviewMutation.mutateAsync({ review, updateData });
      } catch (error) {
        setError('Failed to update review');
        throw error;
      }
    },
    [updateReviewMutation],
  );

  // Function to delete a review
  const deleteReview = useCallback(
    async (productId: number, reviewId: number) => {
      try {
        setError(null);
        await deleteReviewMutation.mutateAsync({ productId, reviewId });
      } catch (error) {
        setError('Failed to delete review');
        throw error;
      }
    },
    [deleteReviewMutation],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    allReviews: allReviewsList,
    searchResults,

    // Loading states
    isLoadingAllReviews: allReviewsQuery.isLoading,
    isLoadingMore,
    isLoadingSearch,
    isUpdatingReview: updateReviewMutation.isPending,
    isDeletingReview: deleteReviewMutation.isPending,

    // Pagination states
    hasMore,
    nextToken,

    // Error states
    error: error || (allReviewsQuery.error instanceof Error ? allReviewsQuery.error.message : null),

    // Functions
    fetchAllReviews,
    fetchReviewsByProductId,
    loadMoreReviews,
    updateReview,
    deleteReview,
    clearError,
  };
};
